import { type ColumnDef } from '@tanstack/react-table';

import { MoneyCell } from '@/components/composed/MoneyCell';
import { DateCell } from '@/components/composed/DateCell';
import {
  StatusBadge,
  type StatusTone,
} from '@/components/composed/StatusBadge';
import type { TableFeatures } from '@/components/composed/DataTable/table-features';
import type { PortalTransaction } from '../schemas/portal';

/*
 * Column definitions for the recent-transactions table (mirrors
 * invoice-columns.tsx). Domain data binds to domain-free composed cells; the
 * DataTable owns rendering. Headers follow the mockup
 * (.agents/mockups/portal-shell.html).
 */

// Map the mock payment-status label to a StatusBadge tone. Unknown labels fall
// back to neutral — a safe default while the status vocabulary is still mock.
function paymentStatusTone(status: string): StatusTone {
  switch (status) {
    case 'Applied':
    case 'Paid':
      return 'success';
    case 'Open':
      return 'warning';
    default:
      return 'neutral';
  }
}

export const portalTransactionColumns: Array<
  ColumnDef<TableFeatures, PortalTransaction>
> = [
  {
    accessorKey: 'transactionDate',
    header: 'Transaction Date',
    // Calendar day (no time-of-day), stored midnight UTC — format in UTC so it
    // never shifts a day in a negative-offset timezone.
    cell: (info) => (
      <DateCell value={info.getValue<string>()} variant="calendar-day" />
    ),
  },
  {
    accessorKey: 'transactionType',
    header: 'Transaction Type',
    cell: (info) => info.getValue<string>(),
  },
  {
    accessorKey: 'coverageMonth',
    header: 'Coverage Month',
    cell: (info) => (
      <DateCell value={info.getValue<string>()} variant="calendar-day" />
    ),
  },
  {
    accessorKey: 'amountMinor',
    header: 'Amount',
    cell: (info) => (
      <MoneyCell
        amountMinor={info.getValue<number>()}
        currency={info.row.original.currency}
      />
    ),
  },
  {
    accessorKey: 'paymentStatus',
    header: 'Payment Status',
    cell: (info) => {
      const status = info.getValue<string>();
      return <StatusBadge label={status} tone={paymentStatusTone(status)} />;
    },
  },
  {
    accessorKey: 'memo',
    header: 'Memo',
    cell: (info) => info.getValue<string>() ?? '',
  },
];
